import { inject, Injectable } from '@angular/core';
import { FetchArgs, PagingToken } from '@aya/controls/aya-table';
import { Observable } from 'rxjs';
import { DocumentAttribute } from '../../../shared/lib/compliance-shared/document-attributes-form/document-attributes-form.store';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { MetadataService } from '../../metadata.service';
import { MetadataConfiguration } from '../../models/acceptance-identifier.model';
import { CandidateVaccine } from '../../models/candidate-vaccine.model';
import { MetadataClient } from '../metadata-client.service';
import { CandidateVaccineDto } from '../models/candidate-vaccine-dto.model';
import { MetadataConfigurationDto } from '../models/metadata-configuration-dto.model';
import { MetadataFieldDto } from '../models/metadata-field-dto.model';
import { MetadataValueDto } from '../models/metadata-value-dto.model';
import { MissingMetadataFilter } from '../models/missing-metadata-filter.model';
import { PagingTokenOfMissingMetadataDto } from '../models/paging-token-of-missing-metadata-dto.model';
import { NSwagMigrationService } from './nswag-migration.service';

// 6 endpoints
@Injectable({ providedIn: 'root' })
export class MetadataNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'MetadataNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationMetadataService;
    private readonly legacy = inject(MetadataService);
    private readonly metadataClient = inject(MetadataClient);

    // ** MetadataController.GetMetadataFields
    getMetadataFields(documentTypeId: number, candidateDocumentId: number): Observable<any | MetadataFieldDto[]> {
        return this.invoke(x => x
            ? this.legacy.getMetadataFields(documentTypeId, candidateDocumentId)
            : this.metadataClient.getMetadataFields(documentTypeId, candidateDocumentId));
    }

    // ** MetadataController.GetFilteredMetadataFields
    getFilteredMetadataFields(documentTypeId: number, documentAttributes: DocumentAttribute[] | MetadataValueDto[]): Observable<any | MetadataFieldDto[]> {
        return this.invoke(x => x
            ? this.legacy.getFilteredMetadataFields(documentTypeId, documentAttributes as DocumentAttribute[])
            : this.metadataClient.getFilteredMetadataFields({ documentTypeId, values: documentAttributes as MetadataValueDto[] }));
    }

    // ** MetadataController.GetMissingMetadata
    getDocsWithMissingMetadata(args: FetchArgs): Observable<PagingToken<any[]> | PagingTokenOfMissingMetadataDto> {
        const filter: MissingMetadataFilter = args.filter ?? {};
        return this.invoke(x => x
            ? this.legacy.getDocsWithMissingMetadata(args)
            : this.metadataClient.getMissingMetadata(
                filter.candidateName, // filter_CandidateName
                filter.documentTypeName, // filter_DocumentTypeName
                filter.documentTypeCategories, // filter_DocumentTypeCategories
                filter.candidateLob, // filter_CandidateLob
                args.pageSize, // pageSize
                args.skip, // skip
                args.sort?.field, // sort_Field
                args.sort?.dir as any, // sort_Type
                undefined // paginated
            ));
    }

    // ** MetadataController.SaveMetadata
    saveMetadata(candidateDocumentId: number, documentTypeId: number, contractId: number | null, metadata: DocumentAttribute[] | MetadataValueDto[]): Observable<any | void> {
        return this.invoke(x => x
            ? this.legacy.saveMetadata(candidateDocumentId, documentTypeId, contractId, metadata as DocumentAttribute[])
            : this.metadataClient.saveMetadata({ candidateDocumentId, documentTypeId, contractId, metadata: metadata as MetadataValueDto[] }));
    }

    // ** MetadataController.GetCovidMetadata
    getCovidMetadata(candidateDocumentId: number, documentTypeId: number): Observable<CandidateVaccine | CandidateVaccineDto> {
        return this.invoke(x => x
            ? this.legacy.getCovidMetadata(candidateDocumentId, documentTypeId)
            : this.metadataClient.getCovidMetadata(candidateDocumentId, documentTypeId));
    }

    // ** MetadataController.GetMetadataConfiguration
    getMetadataConfiguration(documentTypeId: number): Observable<MetadataConfiguration | MetadataConfigurationDto> {
        return this.invoke(x => x
            ? this.legacy.getMetadataConfiguration(documentTypeId)
            : this.metadataClient.getMetadataConfiguration(documentTypeId));
    }
}
